import { useCallback, useContext, useState, type MouseEvent } from 'react';
import { AdminAgentHostTriggersContext } from '@post-office/browser--jira-werewolves/admin-agent-recommendation/setup-trigger-context';
import type {
	AdminAgentSetupSelection,
	PlacementRenderProps,
} from '@post-office/browser--jira-werewolves/admin-agent-recommendation';
import { useIntl } from 'react-intl';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { css } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Button, { IconButton } from '@atlaskit/button/new';
import { SplitButton } from '@atlaskit/button/split-button/split-button';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import { type CustomTriggerProps } from '@atlaskit/dropdown-menu/types';
import CrossIcon from '@atlaskit/icon/core/cross';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import BoardIcon from '@atlaskit/icon/core/board';
import { type JSX } from 'react/jsx-runtime';

import { codingAgentRecommendationMessages as messages } from './messages';
import type { CodingAgentRecommendation } from '../coding-agent-recommendation';
import codingAgentIconSvg from '../assets/icon.svg';

export type CodingAgentRecommendationBannerProps = CodingAgentRecommendation & PlacementRenderProps;

// Prefix for GASv3 `actionSubjectId`s fired by this banner, matching the
// convention used by the sibling crossflow recommendation banners.
const ANALYTICS_ID = 'codingAgentRecommendation';

// ADS's `Button` hardcodes `flex-shrink: 0` on itself and strips any `style`/
// `className`/`css` a consumer passes in, so once it's nested inside `SplitButton`
// there is no CSS-only way to make its label shrink or wrap -- it always renders
// at its full natural width, which can overflow the card. `shouldFitContainer`
// (used below for the plain-button path) does not help here either, since it
// still can't shrink the label text itself, only the button's own box.
//
// Truncating the name here, before it ever reaches Button, is the only way to
// keep the split-button layout intact for a long project name.
const MAX_SPLIT_BUTTON_PROJECT_NAME_LENGTH = 14;

const truncateProjectName = (projectName: string): string =>
	projectName.length > MAX_SPLIT_BUTTON_PROJECT_NAME_LENGTH
		? `${projectName.slice(0, MAX_SPLIT_BUTTON_PROJECT_NAME_LENGTH).trimEnd()}…`
		: projectName;

const containerStyles = css({
	backgroundColor: token('elevation.surface.raised'),
	borderRadius: token('radius.small'),
	border: `${token('border.width')} solid ${token('color.border')}`,
	padding: token('space.200'),
});

// Single row: the icon and the text column take the leading edge, while the
// split button and dismiss control are pushed to the trailing edge. Everything
// is centred against the text column so the controls stay level with it however
// many lines the description wraps to.
//
// `flex-wrap` is the safety valve for narrow containers: the trailing controls
// cannot shrink (a truncated project name is useless), so without wrapping they
// would overflow the card and carry the dismiss button out of view.
const contentRowStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	alignItems: 'center',
	gap: token('space.200'),
});

const iconStyles = css({
	flexShrink: 0,
	objectFit: 'contain',
});

// Takes the slack in the row so the trailing controls are pushed to the right
// edge; `min-width: 0` lets the description wrap instead of forcing the row wider.
//
// The flex basis is the point at which the trailing controls wrap to their own
// line. Without it the column keeps shrinking to the width of its longest word,
// leaving the description stacked one word per line next to an overflowing button.
const stackStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	minWidth: 0,
	flexGrow: 1,
	flexBasis: '16rem',
});

const boldLargeTextStyles = css({
	margin: 0,
	font: token('font.heading.small'),
	fontWeight: token('font.weight.bold'),
});

const bodyTextStyles = css({
	margin: 0,
	font: token('font.body'),
});

// The split button and the dismiss control share the trailing edge of the banner.
// `margin-inline-start: auto` keeps them right-aligned, including once they have
// wrapped onto a line of their own.
//
// ADS buttons will not truncate their label (the inner button keeps `min-width:
// auto`), so a long enough project name makes this group wider than the card. The
// inner `flex-wrap` is what stops that pushing the dismiss button out of view: in
// the worst case it drops to its own line instead of being clipped.
const actionsStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	justifyContent: 'flex-end',
	alignItems: 'center',
	gap: token('space.100'),
	minWidth: 0,
	marginInlineStart: 'auto',
});

// Gives the plain-button path (no dropdown) something to fill: a flex item of its
// own, sized by `flex-grow`/`flex-shrink` rather than by Button's own `flex-shrink:
// 0`, so `shouldFitContainer` has an actual box to match instead of fighting the
// dismiss control for the row's width.
const plainButtonSlotStyles = css({
	display: 'flex',
	minWidth: 0,
	flexGrow: 1,
	flexShrink: 1,
});

const ProjectIcon = ({ iconUrl }: { iconUrl?: string }): JSX.Element => {
	const [failedUrl, setFailedUrl] = useState<string>();
	if (!iconUrl || failedUrl === iconUrl) {
		return <BoardIcon label="" />;
	}

	return (
		<img
			src={iconUrl}
			alt=""
			width="16"
			height="16"
			css={iconStyles}
			onError={() => setFailedUrl(iconUrl)}
			data-testid="jira-coding-agent-project-icon"
		/>
	);
};

export const CodingAgentRecommendationBanner = ({
	projects: recommendedProjects,
	agentId,
	agentAri,
	dismiss,
	onSetUp,
}: CodingAgentRecommendationBannerProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { Setup: SetupTrigger } = useContext(AdminAgentHostTriggersContext);
	const { messageDismissed } = useMessageEvent();
	const { onClosed } = usePostOfficeUserInteractions();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const [isOpening, setIsOpening] = useState(false);
	const [isDismissed, setIsDismissed] = useState(false);
	const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(
		recommendedProjects[0]?.id,
	);

	// Dismissal must be reported to Post Office via `messageDismissed()`, otherwise
	// the placement only drops the banner from local state and the backend will
	// serve the message again on the next load.
	const handleDismiss = useCallback(() => {
		setIsDismissed(true);
		void messageDismissed();
		onClosed({ interactionIdentifier: 'dismissButton', additionalMetadata: {} });
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: `${ANALYTICS_ID}DismissButton`,
			attributes: {
				buttonType: 'secondary',
			},
		});
		dismiss();
	}, [messageDismissed, onClosed, fireAnalyticsEvent, dismiss]);

	// Product analytics only -- deliberately NOT a Post Office interaction signal.
	//
	// Every `usePostOfficeUserInteractions` handler emits `messageEventType: 'clicked'`,
	// and this template's `clickCheckFilter` treats a click as final. Recording the CTA
	// through that API would therefore permanently suppress the banner as soon as the
	// user opens the setup flow, even if they abandon it. Dismissal remains the only
	// action that retires the message.
	//
	// The navigation itself is owned by either the host-supplied `SetupTrigger` (which
	// binds to the button via `ref`) or by `handleSetUp` below, so this never interferes
	// with the click behaviour.
	const trackCtaClick = useCallback(() => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: `${ANALYTICS_ID}CtaButton`,
			attributes: {
				buttonType: 'primary',
			},
		});
	}, [fireAnalyticsEvent]);

	const selectedProject =
		recommendedProjects.find((project) => project.id === selectedProjectId) ??
		recommendedProjects[0];

	if (!selectedProject || isDismissed) {
		return null;
	}

	const setupSelection: AdminAgentSetupSelection = {
		agentId,
		agentAri,
		projectId: selectedProject.id,
		projectKey: selectedProject.projectKey,
		projectType: selectedProject.projectType,
		sourceAri: selectedProject.sourceAri,
		isSimplified: selectedProject.isSimplified,
	};

	const handleSetUp = async (clickEvent: MouseEvent<HTMLElement>): Promise<void> => {
		if (!onSetUp || isOpening) {
			return;
		}
		trackCtaClick();
		const trigger = clickEvent.currentTarget;
		setIsOpening(true);
		try {
			await onSetUp(setupSelection, trigger);
		} catch (error) {
			// Report and leave the banner in place: a failed setup attempt is recoverable,
			// so the user keeps the opportunity to retry rather than losing the card.
			fireAnalyticsEvent({
				eventType: 'operational',
				action: 'failed',
				actionSubject: 'codingAgentSetup',
				actionSubjectId: `${ANALYTICS_ID}Setup`,
				attributes: {
					errorMessage: error instanceof Error ? error.message : String(error),
				},
			});
		} finally {
			setIsOpening(false);
		}
	};

	// A dropdown whose only option is the project already shown is pure noise, so a
	// lone recommendation renders as a plain button instead of a split button.
	const hasProjectChoice = recommendedProjects.length > 1;

	// Only the split-button path needs this: the plain-button path already
	// truncates correctly via CSS (`shouldFitContainer`), so truncating it here
	// too would just shorten names that otherwise fit.
	const displayProjectName = hasProjectChoice
		? truncateProjectName(selectedProject.name)
		: selectedProject.name;

	// Shared by both the plain and split-button cases so the two paths can never
	// drift in label, icon, instrumentation or disabled state.
	const setUpButton = SetupTrigger ? (
		<SetupTrigger key={`${agentId}:${selectedProject.id}`} selection={setupSelection}>
			{({ ref, isDisabled }) => (
				// eslint-disable-next-line @post-office/message-linting/require-instrumented-interactions -- trackCtaClick emits GASv3; a Post Office click would suppress retries after abandoned setup.
				<Button
					ref={ref}
					iconBefore={() => <ProjectIcon iconUrl={selectedProject.iconUrl} />}
					isDisabled={isDisabled}
					onClick={trackCtaClick}
					// Only the plain-button path (no dropdown) can safely fill its row: inside
					// `SplitButton` this would fight the dropdown trigger for width. ADS's Button
					// does not wrap its label, so this is what lets a long project name truncate
					// with an ellipsis instead of overflowing the card.
					shouldFitContainer={!hasProjectChoice}
					testId="jira-coding-agent-recommendation-setup"
				>
					{formatMessage(messages.setUpIn, { projectName: displayProjectName })}
				</Button>
			)}
		</SetupTrigger>
	) : (
		// eslint-disable-next-line @post-office/message-linting/require-instrumented-interactions
		<Button
			iconBefore={() => <ProjectIcon iconUrl={selectedProject.iconUrl} />}
			onClick={handleSetUp}
			isLoading={isOpening}
			isDisabled={!onSetUp}
			shouldFitContainer={!hasProjectChoice}
			testId="jira-coding-agent-recommendation-setup"
		>
			{formatMessage(messages.setUpIn, { projectName: displayProjectName })}
		</Button>
	);

	return (
		<div css={containerStyles} data-testid="jira-coding-agent-recommendation">
			<div css={contentRowStyles}>
				<img
					src={codingAgentIconSvg}
					alt=""
					width="40"
					height="40"
					css={iconStyles}
					data-testid="jira-coding-agent-recommendation-icon"
				/>
				<div css={stackStyles}>
					<p css={boldLargeTextStyles}>{formatMessage(messages.title)}</p>
					<p css={bodyTextStyles}>{formatMessage(messages.description)}</p>
				</div>
				<div css={actionsStyles}>
					{hasProjectChoice ? (
						<SplitButton appearance="default">
							{setUpButton}
							<DropdownMenu<HTMLButtonElement>
								shouldRenderToParent
								trigger={({
									triggerRef,
									...triggerProps
								}: CustomTriggerProps<HTMLButtonElement>) => (
									<IconButton
										{...triggerProps}
										ref={triggerRef}
										isDisabled={isOpening}
										icon={ChevronDownIcon}
										label={formatMessage(messages.chooseProject)}
										testId="jira-coding-agent-recommendation-toggle"
									/>
								)}
							>
								<DropdownItemGroup>
									{recommendedProjects.map((project) => (
										<DropdownItem
											key={project.id}
											elemBefore={<ProjectIcon iconUrl={project.iconUrl} />}
											isSelected={project.id === selectedProject.id}
											onClick={() => {
												setSelectedProjectId(project.id);
												fireAnalyticsEvent({
													eventType: 'ui',
													action: 'clicked',
													actionSubject: 'dropdownItem',
													actionSubjectId: `${ANALYTICS_ID}ProjectDropdownItem`,
												});
											}}
										>
											{project.name}
										</DropdownItem>
									))}
								</DropdownItemGroup>
							</DropdownMenu>
						</SplitButton>
					) : (
						<div css={plainButtonSlotStyles}>{setUpButton}</div>
					)}
					<IconButton
						appearance="subtle"
						spacing="compact"
						icon={CrossIcon}
						label={formatMessage(messages.dismiss)}
						onClick={handleDismiss}
						testId="jira-coding-agent-recommendation-dismiss"
					/>
				</div>
			</div>
		</div>
	);
};
